import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Receipt, Plus, CheckCircle, XCircle, DollarSign, Clock, Upload } from 'lucide-react';

import API_URL from '../utils/api';

const CATEGORIES = [
    { value: 'travel', label: 'Travel' }, { value: 'food', label: 'Food & Meals' },
    { value: 'accommodation', label: 'Accommodation' }, { value: 'equipment', label: 'Equipment' },
    { value: 'office_supplies', label: 'Office Supplies' }, { value: 'communication', label: 'Communication' },
    { value: 'other', label: 'Other' },
];

const Expenses = () => {
    const { user, canManage } = useAuth();
    const [expenses, setExpenses] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ title: '', amount: '', category: 'other', description: '', expense_date: '' });

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [expRes, sumRes] = await Promise.all([
                axios.get(`${API_URL}/api/expenses`, { withCredentials: true }),
                axios.get(`${API_URL}/api/expenses/summary`, { withCredentials: true })
            ]);
            setExpenses(expRes.data);
            setSummary(sumRes.data);
        } catch (error) { logger.error(error); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/expenses`, {
                ...form, amount: parseFloat(form.amount),
                expense_date: form.expense_date ? new Date(form.expense_date).toISOString() : null
            }, { withCredentials: true });
            setDialogOpen(false);
            setForm({ title: '', amount: '', category: 'other', description: '', expense_date: '' });
            fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); }
    };

    const handleApprove = async (id, status) => {
        const remarks = status === 'rejected' ? prompt('Reason for rejection:') : '';
        if (status === 'rejected' && remarks === null) return;
        try {
            await axios.put(`${API_URL}/api/expenses/${id}/approve`, { status, remarks }, { withCredentials: true });
            fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); }
    };

    const uploadReceipt = async (expenseId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API_URL}/api/expenses/${expenseId}/receipt`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            fetchData();
        } catch (error) { alert('Upload failed'); }
    };

    const getStatusBadge = (s) => {
        const colors = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800', paid: 'bg-blue-100 text-blue-800' };
        return colors[s] || colors.pending;
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading...</div></div>;

    return (
        <div className="space-y-6" data-testid="expenses-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Expenses</h1>
                    <p className="text-slate-500 mt-1">{canManage ? 'Manage expense reimbursements' : 'Submit and track your expenses'}</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild><Button className="bg-brand hover:bg-brand rounded-none" data-testid="submit-expense-btn"><Plus className="h-4 w-4 mr-2" />Submit Expense</Button></DialogTrigger>
                    <DialogContent className="rounded-none max-w-lg">
                        <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Submit Expense</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Submit an expense claim with amount, category and receipt.</DialogDescription></DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Title</Label>
                                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-none mt-1" required data-testid="expense-title-input" /></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Amount (Rs.)</Label>
                                    <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="rounded-none mt-1" required data-testid="expense-amount-input" /></div>
                                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</Label>
                                    <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                                        <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent className="rounded-none">
                                            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select></div>
                            </div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Date</Label>
                                <Input type="date" value={form.expense_date} onChange={(e) => setForm({...form, expense_date: e.target.value})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
                                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-none mt-1" rows={2} /></div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-expense-btn">Submit</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-xs font-bold uppercase text-slate-500">Pending</p><p className="text-2xl font-black text-amber-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{summary.pending_count}</p><p className="text-xs text-slate-500 mt-0.5">Rs.{(summary.total_pending || 0).toLocaleString()}</p></CardContent></Card>
                    <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-xs font-bold uppercase text-slate-500">Approved</p><p className="text-2xl font-black text-green-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{summary.approved_count}</p><p className="text-xs text-slate-500 mt-0.5">Rs.{(summary.total_approved || 0).toLocaleString()}</p></CardContent></Card>
                    <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-xs font-bold uppercase text-slate-500">Total Pending Amt</p><p className="text-2xl font-black text-slate-900 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{(summary.total_pending || 0).toLocaleString()}</p></CardContent></Card>
                    <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-xs font-bold uppercase text-slate-500">Total Approved Amt</p><p className="text-2xl font-black text-slate-900 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{(summary.total_approved || 0).toLocaleString()}</p></CardContent></Card>
                </div>
            )}

            <Card className="rounded-none border-slate-200">
                <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full">
                        <thead><tr className="border-b border-slate-200 bg-slate-50">
                            <th className="text-left p-3 text-xs font-bold uppercase text-slate-500">Title</th>
                            {canManage && <th className="text-left p-3 text-xs font-bold uppercase text-slate-500">Employee</th>}
                            <th className="text-left p-3 text-xs font-bold uppercase text-slate-500">Category</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Amount</th>
                            <th className="text-left p-3 text-xs font-bold uppercase text-slate-500">Date</th>
                            <th className="text-center p-3 text-xs font-bold uppercase text-slate-500">Receipt</th>
                            <th className="text-center p-3 text-xs font-bold uppercase text-slate-500">Status</th>
                            {canManage && <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Actions</th>}
                        </tr></thead>
                        <tbody>
                            {expenses.map(exp => (
                                <tr key={exp._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`expense-${exp._id}`}>
                                    <td className="p-3"><p className="font-medium text-slate-900">{exp.title}</p>{exp.description && <p className="text-xs text-slate-500">{exp.description}</p>}</td>
                                    {canManage && <td className="p-3 text-sm text-slate-700">{exp.employee_name}</td>}
                                    <td className="p-3"><Badge className="rounded-none bg-slate-100 text-slate-700 text-xs capitalize">{exp.category?.replace('_', ' ')}</Badge></td>
                                    <td className="p-3 text-right font-bold text-slate-900">Rs.{exp.amount?.toLocaleString()}</td>
                                    <td className="p-3 text-sm text-slate-600">{exp.expense_date ? new Date(exp.expense_date).toLocaleDateString() : '-'}</td>
                                    <td className="p-3 text-center">
                                        {exp.receipt_path ? <Badge className="rounded-none bg-green-100 text-green-700 text-xs">Attached</Badge> : (
                                            exp.employee_id === user?._id || exp.employee_id === user?.id ? (
                                                <label className="cursor-pointer text-brand text-xs hover:underline">
                                                    <Upload className="h-3 w-3 inline mr-1" />Upload
                                                    <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadReceipt(exp._id, e.target.files[0])} accept=".pdf,.jpg,.jpeg,.png" />
                                                </label>
                                            ) : <span className="text-xs text-slate-400">None</span>
                                        )}
                                    </td>
                                    <td className="p-3 text-center"><Badge className={`rounded-none ${getStatusBadge(exp.status)}`}>{exp.status}</Badge></td>
                                    {canManage && (
                                        <td className="p-3 text-right">
                                            {exp.status === 'pending' && (
                                                <div className="flex justify-end gap-1">
                                                    <Button size="sm" className="rounded-none bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={() => handleApprove(exp._id, 'approved')} data-testid={`approve-expense-${exp._id}`}><CheckCircle className="h-3 w-3 mr-1" />Approve</Button>
                                                    <Button size="sm" variant="outline" className="rounded-none h-7 text-xs text-red-600" onClick={() => handleApprove(exp._id, 'rejected')} data-testid={`reject-expense-${exp._id}`}><XCircle className="h-3 w-3 mr-1" />Reject</Button>
                                                </div>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {expenses.length === 0 && <tr><td colSpan={canManage ? 8 : 6} className="p-8 text-center text-slate-500">No expenses found</td></tr>}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
};

export default Expenses;
